import { Fragment, ReactElement, useCallback, useMemo, useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import SectionHeader from '../../components/SectionHeader';
import Spinner from '../../components/Spinner';
import Button from '../../components/Button';
import { FitBounds, BoundsListener, MapAutoResize } from '../../components/MapBoundsComponents';
import {
  useGatesWMinRoute, useGateDuplicateAnalysis,
  useMergeGates, useCleanupOrphanGates, useMergeIdenticalGates,
  useMergeRoutes,
} from '../../hooks/useGates';
import {
  useRoutesForManagement, useUpdateRoute, useRetrieveAddresses,
} from '../../hooks/useRoutes';
import { useSetGatePinned, useUpdateGate } from '../../hooks/useGates';
import { useGateSummariesWithCluster, useGateFocus, useMapBounds } from '../../hooks/useGateMap';
import { createGateIcon } from '../../utils/gateMapUtils';
import { deleteGates } from '../../api/gates';
import { deleteRoute } from '../../api/routes';
import markerIconPng from '../../icons/marker-icon-green.png';
import endMarkerIconPng from '../../icons/marker-icon-red.png';
import { GateSummaryWithCluster } from '../../types/GateSummary';
import { DuplicateGroup, Gate } from '../../types/Gate';
import { RouteWithGates } from '../../api/routes';
import {
  HiOutlineCheckCircle, HiOutlineChevronRight, HiOutlinePencil, HiOutlineStar, HiOutlineXMark, HiStar,
} from 'react-icons/hi2';
import { clusterRoutes } from '../../utils/routeClustering';
import { ROUTE_MERGE_MAX_DISTANCE_M, findRouteMergeCandidates } from '../../utils/routeMergeCandidates';
import { routeHasPinnedGate, sortClustersPinnedFirst } from '../../utils/pinnedGates';

/* ─── Main Component ─── */

export default function RouteManagement(): ReactElement {
  const {
    data: routes,
    isPending,
    isError,
    refetch,
  } = useRoutesForManagement();

  // Gate data still needed for map and duplicate analysis
  const { data: gatesRaw } = useGatesWMinRoute();
  const gateSummaries = useGateSummariesWithCluster(gatesRaw);

  const [expandedClusters, setExpandedClusters] = useState<Set<number>>(
    new Set(),
  );
  const [filterMode, setFilterMode] = useState<
    'all' | 'merged' | 'unused' | 'duplicates'
  >('all');
  const [editMode, setEditMode] = useState(false);
  const [selectedRouteIds, setSelectedRouteIds] = useState<Set<string>>(
    new Set(),
  );
  const [expandedDupGroups, setExpandedDupGroups] = useState<Set<number>>(
    new Set(),
  );

  const {
    data: dupAnalysis,
    isPending: isDupPending,
    isError: isDupError,
  } = useGateDuplicateAnalysis(filterMode === 'duplicates');

  const mergeMutation = useMergeGates();
  const cleanupMutation = useCleanupOrphanGates();
  const mergeIdenticalMutation = useMergeIdenticalGates();
  const mergeRoutesMutation = useMergeRoutes();
  const updateRouteMutation = useUpdateRoute();
  const updateGateMutation = useUpdateGate();
  const setGatePinnedMutation = useSetGatePinned();
  const retrieveAddressesMutation = useRetrieveAddresses();

  /* ── Duplicate-panel merge (unchanged) ── */
  const handleMergeGroup = useCallback(
    (gateIds: string[]) => {
      const confirmed = window.confirm(
        `Merge ${gateIds.length} gates into 1? ` +
          'Sessions will be re-pointed to the gate with the most sessions.',
      );
      if (!confirmed) return;
      mergeMutation.mutate(gateIds, {
        onSuccess: () => {
          setExpandedDupGroups(new Set());
          refetch();
        },
        onError: (err) => {
          console.error('Failed to merge gates:', err);
          alert('Failed to merge gates. Please try again.');
        },
      });
    },
    [mergeMutation, refetch],
  );

  /* ── Filter routes ── */
  const filteredRoutes = useMemo(() => {
    if (!routes) return [];
    switch (filterMode) {
      case 'unused':
        return routes.filter((r) => r.total_session_count === 0);
      case 'merged':
        return routes.filter(
          (r) =>
            r.gates &&
            r.gates.some((g) => g.creation_mode === 3),
        );
      case 'all':
      default:
        return routes;
    }
  }, [routes, filterMode]);

  const clusteredRoutes = useMemo(
    () => sortClustersPinnedFirst(clusterRoutes(filteredRoutes)),
    [filteredRoutes],
  );

  /* ── Edit-mode handlers ── */

  const handleMergeIdentical = useCallback(
    (routeId: string) => {
      const ok = window.confirm(
        'Merge gates with identical coordinates on this route?',
      );
      if (!ok) return;
      mergeIdenticalMutation.mutate(routeId, {
        onSuccess: (res) => {
          refetch();
          if (res.groups_merged === 0) {
            alert('No identical gates found on this route.');
          }
        },
        onError: (err) => {
          console.error('Failed to merge identical gates:', err);
          alert('Failed to merge identical gates.');
        },
      });
    },
    [mergeIdenticalMutation, refetch],
  );

  const handleSelectUnused = useCallback(() => {
    if (!routes) return;
    const unused = new Set<string>();
    for (const r of routes) {
      if (r.total_session_count === 0) unused.add(r.id);
    }
    setSelectedRouteIds(unused);
  }, [routes]);

  const handleDeleteSelectedRoutes = useCallback(async () => {
    if (!routes) return;
    const gateIds: string[] = [];
    const deletableRouteIds: string[] = [];
    let skippedWithSessions = 0;
    for (const r of routes) {
      if (!selectedRouteIds.has(r.id)) continue;
      if (r.total_session_count > 0) {
        skippedWithSessions++;
        continue;
      }
      deletableRouteIds.push(r.id);
      if (r.gates) {
        for (const g of r.gates) gateIds.push(g.id);
      }
    }
    if (deletableRouteIds.length === 0) {
      if (skippedWithSessions > 0) {
        alert(
          `${skippedWithSessions} route(s) have sessions ` +
            'and cannot be deleted.',
        );
      }
      return;
    }
    let msg = `Delete ${deletableRouteIds.length} orphan route(s)`;
    if (gateIds.length) msg += ` and ${gateIds.length} gate(s)`;
    msg += '?';
    if (skippedWithSessions > 0) {
      msg += ` (${skippedWithSessions} route(s) with sessions skipped)`;
    }
    if (!window.confirm(msg)) return;
    try {
      if (gateIds.length > 0) {
        await deleteGates(gateIds);
      }
      await Promise.all(
        deletableRouteIds.map((id) => deleteRoute(id)),
      );
      setSelectedRouteIds(new Set());
      refetch();
    } catch (error) {
      console.error('Failed to delete:', error);
      alert('Failed to delete. Please try again.');
    }
  }, [routes, selectedRouteIds, refetch]);

  /* ── Which of the selected routes can actually be merged ── */
  const mergeSelection = useMemo(() => {
    const selected = (routes ?? []).filter((r) => selectedRouteIds.has(r.id));
    return findRouteMergeCandidates(selected);
  }, [routes, selectedRouteIds]);
  const canMergeSelection = mergeSelection.candidates.length >= 2;
  // Delete is only offered when nothing selected is in use: a route with
  // sessions cannot be deleted, so the button would only ever skip it.
  const selectionHasSessions = useMemo(
    () => (routes ?? []).some((r) => selectedRouteIds.has(r.id) && r.total_session_count > 0),
    [routes, selectedRouteIds],
  );
  const mergeDisabledReason = canMergeSelection
    ? undefined
    : `Selected routes are more than ${ROUTE_MERGE_MAX_DISTANCE_M} m apart and cannot be merged`;

  const handleMergeRoutes = useCallback(() => {
    const { candidates, excluded } = mergeSelection;
    if (candidates.length < 2) return;
    const ids = candidates.map((r) => r.id);
    let msg = `Merge ${ids.length} routes into one averaged route? ` +
      'Gates will be re-assigned where they intersect.';
    if (excluded.length) {
      const names = excluded.map(({ route, distanceM }) => {
        const label = route.description || route.address || route.id;
        const why = distanceM == null ? 'no gate position' : `${distanceM} m away`;
        return `  - ${label} (${why})`;
      });
      msg += `\n\n${excluded.length} selected route(s) cannot be merged ` +
        `(too far from the others, limit ${ROUTE_MERGE_MAX_DISTANCE_M} m) and will be left out:\n` +
        names.join('\n');
    }
    if (!window.confirm(msg)) return;
    mergeRoutesMutation.mutate(ids, {
      onSuccess: (res) => {
        setSelectedRouteIds(new Set());
        refetch();
        if (res.gates_reassigned === 0 && res.gates_skipped > 0) {
          alert(
            `Merged route created, but none of its ${res.gates_skipped} gate(s) ` +
              'could be placed on it, so the source routes were kept.',
          );
        }
      },
      onError: (err: any) => {
        console.error('Failed to merge routes:', err);
        const msg =
          err?.response?.data?.message ||
          'Failed to merge routes.';
        alert(msg);
      },
    });
  }, [mergeSelection, mergeRoutesMutation, refetch]);

  const handleRetrieveAddresses = useCallback(() => {
    const ids = Array.from(selectedRouteIds);
    if (!ids.length) return;
    retrieveAddressesMutation.mutate(ids, {
      onSuccess: (res) => {
        refetch();
        alert(`Addresses retrieved for ${res.updated} route(s).`);
      },
      onError: () => alert('Failed to retrieve addresses.'),
    });
  }, [selectedRouteIds, retrieveAddressesMutation, refetch]);

  const handleUpdateRoute = useCallback(
    (routeId: string, data: { description: string; notes: string | null }) => {
      updateRouteMutation.mutate(
        { routeId, data },
        { onError: () => alert('Failed to update the route.') },
      );
    },
    [updateRouteMutation],
  );

  const handleUpdateGate = useCallback(
    (gateId: string, data: Pick<Partial<Gate>, 'name' | 'notes'>) => {
      updateGateMutation.mutate(
        { gateId, data },
        { onError: () => alert('Failed to update the gate.') },
      );
    },
    [updateGateMutation],
  );

  const handleSetGatePinned = useCallback(
    (gateId: string, pinned: boolean) => {
      setGatePinnedMutation.mutate(
        { gateId, pinned },
        { onError: () => alert('Failed to update the pinned gate.') },
      );
    },
    [setGatePinnedMutation],
  );

  /* ── Cleanup orphans ── */
  const handleCleanupOrphans = useCallback(() => {
    const count = filteredRoutes.length;
    const ok = window.confirm(
      `Delete all ${count} unused route(s) and their gates? ` +
        'This also removes orphaned routes.',
    );
    if (!ok) return;
    cleanupMutation.mutate(undefined, {
      onSuccess: () => {
        setSelectedRouteIds(new Set());
        refetch();
      },
      onError: (err) => {
        console.error('Failed to cleanup orphan gates:', err);
        alert('Failed to cleanup orphan gates.');
      },
    });
  }, [cleanupMutation, filteredRoutes.length, refetch]);

  /* ── Selection toggles ── */
  const toggleRouteSelection = useCallback((routeId: string) => {
    setSelectedRouteIds((prev) => {
      const next = new Set(prev);
      if (next.has(routeId)) next.delete(routeId);
      else next.add(routeId);
      return next;
    });
  }, []);

  const toggleCluster = useCallback((clusterId: number) => {
    setExpandedClusters((prev) => {
      const next = new Set(prev);
      if (next.has(clusterId)) next.delete(clusterId);
      else next.add(clusterId);
      return next;
    });
  }, []);

  /* ── Map ── */
  const {
    activeGateId,
    selectedGate,
    fitBoundsTrigger,
    handleFocusGate,
    handleZoomAll,
  } = useGateFocus(gateSummaries);

  const { bounds, mapCenter, showBothGates, handleMapBoundsChange } =
    useMapBounds(
      gateSummaries,
      selectedGate,
      selectedGate ? 'selected' : 'all',
    );

  // The number each gate carries on the map, so the list can show the same one.
  const gateOrder = useMemo(
    () => new Map(gateSummaries.map((summary) => [summary.id, summary.order])),
    [gateSummaries],
  );

  const getGateIcon = useCallback(
    (type: 'start' | 'end', label: string) =>
      createGateIcon(type, label, markerIconPng, endMarkerIconPng),
    [],
  );

  /* ── Render ── */
  return (
    <div className="cr-w-full">
      <SectionHeader title="Route Management" isLoading={isPending} />

      {/* Side by side from lg up: routes on the left, the map on the right and
          sticky so it stays in view while the list scrolls. Stacked below lg. */}
      <div className="cr-mt-6 cr-grid cr-gap-6 lg:cr-grid-cols-2 lg:cr-items-start">
        {/* ── Map ── */}
        <div
          className={
            'cr-relative cr-h-[500px] cr-overflow-hidden ' +
            'cr-rounded-xl cr-bg-white cr-shadow-sm ' +
            'lg:cr-sticky lg:cr-top-4 lg:cr-order-2 lg:cr-h-[calc(100vh-8rem)]'
          }
        >
          {isPending && (
            <div
              className={
                'cr-flex cr-h-full cr-items-center cr-justify-center'
              }
            >
              <Spinner isFetching={true} />
            </div>
          )}
          {isError && !isPending && (
            <div
              className={
                'cr-flex cr-h-full cr-items-center ' +
                'cr-justify-center cr-text-red-600'
              }
            >
              Unable to load routes. Please try again later.
            </div>
          )}
          {!isError && !isPending && gateSummaries.length === 0 && (
            <div
              className={
                'cr-flex cr-h-full cr-items-center cr-justify-center'
              }
            >
              No gates available for this team yet.
            </div>
          )}
          {!isError && !isPending && gateSummaries.length > 0 && (
            <>
              {selectedGate && (
                <div className="cr-absolute cr-right-4 cr-top-4 cr-z-[1000] cr-flex cr-gap-2">
                  <GateStarButton
                    withLabel
                    pinned={selectedGate.gate.pinned}
                    onToggle={() => handleSetGatePinned(selectedGate.gate.id, !selectedGate.gate.pinned)}
                  />
                  {gateSummaries.length > 1 && (
                    <button
                      type="button"
                      onClick={handleZoomAll}
                      className={
                        'cr-rounded-md cr-bg-white cr-px-3 cr-py-1 ' +
                        'cr-text-xs cr-font-semibold cr-text-blue-900 ' +
                        'cr-shadow-sm hover:cr-bg-cirrusBlue-25'
                      }
                    >
                      All Gates
                    </button>
                  )}
                </div>
              )}
              <MapContainer
                center={mapCenter}
                zoom={13}
                scrollWheelZoom
                className="cr-h-full cr-w-full"
              >
                <MapAutoResize />
                <FitBounds bounds={bounds} trigger={fitBoundsTrigger} />
                <BoundsListener onBoundsChange={handleMapBoundsChange} />
                <TileLayer
                  attribution={
                    '&copy; <a href="https://www.openstreetmap.org/' +
                    'copyright">OpenStreetMap</a> contributors'
                  }
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {gateSummaries.map((marker) => (
                  <Fragment key={marker.id}>
                    <Marker
                      position={marker.start}
                      icon={getGateIcon('start', String(marker.order))}
                    >
                      <GatePopup marker={marker} label="Start gate" onSetPinned={handleSetGatePinned} />
                    </Marker>
                    {showBothGates && marker.end && (
                      <Marker
                        position={marker.end}
                        icon={getGateIcon('end', String(marker.order))}
                      >
                        <GatePopup marker={marker} label="End gate" onSetPinned={handleSetGatePinned} />
                      </Marker>
                    )}
                  </Fragment>
                ))}
              </MapContainer>
            </>
          )}
        </div>

        {/* ── Filter bar + Edit ── */}
        <div className="cr-min-w-0 cr-rounded-xl cr-bg-white cr-p-6 cr-shadow-sm lg:cr-order-1">
          <div
            className={
              'cr-mb-4 cr-flex cr-flex-wrap cr-items-center cr-justify-between cr-gap-3'
            }
          >
            <div className="cr-flex cr-flex-wrap cr-items-center cr-gap-6">
              <FilterRadio
                value="all"
                label="All"
                current={filterMode}
                onChange={() => {
                  setFilterMode('all');
                  setSelectedRouteIds(new Set());
                }}
              />
              <FilterRadio
                value="merged"
                label="Merged"
                current={filterMode}
                onChange={() => {
                  setFilterMode('merged');
                  setSelectedRouteIds(new Set());
                }}
              />
              <FilterRadio
                value="unused"
                label="Unused"
                current={filterMode}
                onChange={() => {
                  setFilterMode('unused');
                  setSelectedRouteIds(new Set());
                }}
              />
              <FilterRadio
                value="duplicates"
                label="Duplicates"
                current={filterMode}
                onChange={() => {
                  setFilterMode('duplicates');
                  setSelectedRouteIds(new Set());
                  setExpandedDupGroups(new Set());
                }}
              />
              <Button
                onClick={() => setEditMode((p) => !p)}
                className={
                  editMode
                    ? 'cr-bg-cirrusBlue-100 cr-text-white'
                    : ''
                }
              >
                Manage
              </Button>
            </div>

            {/* ── Edit-mode action buttons ── */}
            <div className="cr-flex cr-flex-wrap cr-gap-2">
              {editMode && selectedRouteIds.size >= 2 && (
                <Button
                  onClick={handleMergeRoutes}
                  disabled={mergeRoutesMutation.isPending || !canMergeSelection}
                  title={mergeDisabledReason}
                >
                  {mergeRoutesMutation.isPending
                    ? 'Merging...'
                    : `Merge Selected (${mergeSelection.candidates.length}/${selectedRouteIds.size})`}
                </Button>
              )}
              {editMode && selectedRouteIds.size > 0 && (
                <Button
                  onClick={handleRetrieveAddresses}
                  disabled={retrieveAddressesMutation.isPending}
                >
                  {retrieveAddressesMutation.isPending
                    ? 'Retrieving...'
                    : `Retrieve Addresses (${selectedRouteIds.size})`}
                </Button>
              )}
              {editMode && (
                <Button onClick={handleSelectUnused}>
                  Select Unused
                </Button>
              )}
              {editMode && selectedRouteIds.size > 0 && !selectionHasSessions && (
                <Button
                  onClick={handleDeleteSelectedRoutes}
                  className={
                    'cr-bg-red-600 cr-text-white hover:cr-bg-red-700'
                  }
                >
                  Delete Selected ({selectedRouteIds.size})
                </Button>
              )}
              {!editMode &&
                filterMode === 'unused' &&
                filteredRoutes.length > 0 && (
                  <Button
                    onClick={handleCleanupOrphans}
                    className={
                      'cr-bg-red-600 cr-text-white ' +
                      'hover:cr-bg-red-700'
                    }
                    disabled={cleanupMutation.isPending}
                  >
                    {cleanupMutation.isPending
                      ? 'Deleting...'
                      : `Delete All Unused (${filteredRoutes.length})`}
                  </Button>
                )}
            </div>
          </div>

          {/* ── Duplicate analysis panel ── */}
          {filterMode === 'duplicates' && (
            <DuplicateAnalysisPanel
              dupAnalysis={dupAnalysis ?? null}
              isPending={isDupPending}
              isError={isDupError}
              expandedGroups={expandedDupGroups}
              onToggleGroup={(idx) => {
                setExpandedDupGroups((prev) => {
                  const next = new Set(prev);
                  if (next.has(idx)) next.delete(idx);
                  else next.add(idx);
                  return next;
                });
              }}
              onMergeGroup={handleMergeGroup}
              isMerging={mergeMutation.isPending}
            />
          )}

          {/* ── Route-centric list ── */}
          {filterMode !== 'duplicates' && (
            <div className="cr-flex cr-flex-col cr-gap-2">
              {clusteredRoutes.map((cluster) => {
                const isClusterExpanded = expandedClusters.has(
                  cluster.clusterId,
                );
                return (
                  <div
                    key={cluster.clusterId}
                    className={
                      'cr-overflow-hidden cr-rounded-lg ' +
                      'cr-border cr-border-gray-200'
                    }
                  >
                    {/* Cluster header */}
                    <button
                      onClick={() => toggleCluster(cluster.clusterId)}
                      className={
                        'cr-flex cr-w-full cr-items-center ' +
                        'cr-justify-between cr-bg-gray-50 cr-p-4 ' +
                        'cr-transition-colors hover:cr-bg-gray-100'
                      }
                    >
                      <div className="cr-flex cr-items-center cr-gap-2">
                        <HiOutlineChevronRight
                          className={
                            'cr-h-5 cr-w-5 cr-text-gray-500 cr-transition-transform' +
                            (isClusterExpanded ? ' cr-rotate-90' : '')
                          }
                        />
                        <span className="cr-text-2xl cr-font-semibold">
                          {cluster.clusterName}
                        </span>
                        <span className="cr-text-lg cr-text-gray-500">
                          ({cluster.routes.length} route
                          {cluster.routes.length !== 1 ? 's' : ''})
                        </span>
                      </div>
                    </button>

                    {/* Routes inside cluster */}
                    {isClusterExpanded && (
                      <div className="cr-border-t cr-border-gray-200">
                        {cluster.routes.map((route) => (
                          <RouteRow
                            key={route.id}
                            route={route}
                            isSelected={selectedRouteIds.has(route.id)}
                            editMode={editMode}
                            activeGateId={activeGateId}
                            isMerging={
                              mergeIdenticalMutation.isPending
                            }
                            onToggleSelect={() =>
                              toggleRouteSelection(route.id)
                            }
                            gateOrder={gateOrder}
                            onFocusGate={handleFocusGate}
                            onMergeIdentical={() =>
                              handleMergeIdentical(route.id)
                            }
                            onUpdateRoute={(data) => handleUpdateRoute(route.id, data)}
                            onUpdateGate={handleUpdateGate}
                            onSetGatePinned={handleSetGatePinned}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Sub-components ─── */

function FilterRadio({
  value,
  label,
  current,
  onChange,
}: {
  value: string;
  label: string;
  current: string;
  onChange: () => void;
}) {
  return (
    <label className="cr-flex cr-cursor-pointer cr-items-center cr-gap-2">
      <input
        type="radio"
        name="gateFilter"
        value={value}
        checked={current === value}
        onChange={onChange}
        className={
          'cr-h-4 cr-w-4 cr-border-gray-300 ' +
          'cr-text-cirrusBlue-100 focus:cr-ring-cirrusBlue-100'
        }
      />
      <span className="cr-text-sm cr-font-medium">{label}</span>
    </label>
  );
}

/** The pencil that opens a route's or gate's name-and-notes editor. */
function EditPencil({ onClick, title }: { onClick: () => void; title: string }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      title={title}
      aria-label={title}
      className="cr-rounded cr-p-1 cr-text-blue-900 hover:cr-bg-gray-200"
    >
      <HiOutlinePencil className="cr-h-4 cr-w-4" />
    </button>
  );
}

/**
 * Inline editor for a name and its notes. Enter in the name field or the tick saves,
 * Escape or the cross cancels. Empty notes save as null; clicks never reach the row underneath.
 */
function NameNotesForm({
  name,
  notes,
  nameLabel,
  namePlaceholder,
  nameMaxLength,
  onSave,
  onCancel,
}: {
  name: string;
  notes: string | null;
  nameLabel: string;
  namePlaceholder: string;
  nameMaxLength: number;
  onSave: (name: string, notes: string | null) => void;
  onCancel: () => void;
}) {
  const [nameDraft, setNameDraft] = useState(name);
  const [notesDraft, setNotesDraft] = useState(notes ?? '');

  const save = () => onSave(nameDraft.trim(), notesDraft.trim() || null);
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onCancel();
  };
  const inputClass = 'cr-w-full cr-rounded cr-border cr-border-gray-300 cr-px-2 cr-py-1 cr-text-sm';

  return (
    <div
      className="cr-flex cr-flex-col cr-gap-2 cr-rounded-md cr-bg-gray-50 cr-p-3"
      onClick={(e) => e.stopPropagation()}
    >
      <label className="cr-text-xs cr-font-semibold cr-text-gray-700">
        {nameLabel}
        <input
          type="text"
          value={nameDraft}
          maxLength={nameMaxLength}
          onChange={(e) => setNameDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            onKeyDown(e);
          }}
          autoFocus
          placeholder={namePlaceholder}
          className={inputClass + ' cr-mt-1 cr-font-normal'}
        />
      </label>
      <label className="cr-text-xs cr-font-semibold cr-text-gray-700">
        Notes
        <textarea
          value={notesDraft}
          maxLength={1024}
          rows={2}
          onChange={(e) => setNotesDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Notes..."
          className={inputClass + ' cr-mt-1 cr-font-normal'}
        />
      </label>
      <div className="cr-flex cr-gap-2">
        <button type="button" onClick={save} title="Save" aria-label="Save" className="cr-text-green-700">
          <HiOutlineCheckCircle className="cr-h-6 cr-w-6" />
        </button>
        <button type="button" onClick={onCancel} title="Cancel" aria-label="Cancel" className="cr-text-gray-600">
          <HiOutlineXMark className="cr-h-6 cr-w-6" />
        </button>
      </div>
    </div>
  );
}

/** A gate's map number, drawn like the start-gate marker label so the two read as one. */
function GateNumber({ order }: { order: number | undefined }) {
  if (order == null) return null;
  return (
    <span
      title={`Gate ${order} on the map`}
      className={
        'cr-inline-flex cr-min-w-[1.5rem] cr-items-center cr-justify-center cr-rounded-full ' +
        'cr-bg-teal-700 cr-px-1.5 cr-py-0.5 cr-text-xs cr-font-bold cr-leading-none cr-text-white'
      }
    >
      {order}
    </span>
  );
}

/**
 * Star toggle for one gate pair. Starring is stored per gate (`gates.pinned`); a route
 * shows as starred when any of its gates is. With `withLabel` it renders as a labelled
 * button for the map overlay, otherwise as a bare icon for rows and popups.
 */
function GateStarButton({
  pinned,
  onToggle,
  withLabel = false,
}: {
  pinned: boolean;
  onToggle: () => void;
  withLabel?: boolean;
}) {
  const icon = pinned ? (
    <HiStar className="cr-h-4 cr-w-4 cr-text-amber-400" />
  ) : (
    <HiOutlineStar className="cr-h-4 cr-w-4 cr-text-gray-400 group-hover:cr-text-amber-400" />
  );
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      title={pinned ? 'Unstar this gate' : 'Star this gate (and its route) for quick selection'}
      aria-label={pinned ? 'Unstar gate' : 'Star gate'}
      aria-pressed={pinned}
      className={
        'cr-group' +
        (withLabel
          ? ' cr-flex cr-items-center cr-gap-1 cr-rounded-md cr-bg-white cr-px-3 cr-py-1 ' +
            'cr-text-xs cr-font-semibold cr-text-blue-900 cr-shadow-sm hover:cr-bg-cirrusBlue-25'
          : '')
      }
    >
      {icon}
      {withLabel && (pinned ? 'Starred' : 'Star')}
    </button>
  );
}

function GatePopup({
  marker,
  label,
  onSetPinned,
}: {
  marker: GateSummaryWithCluster;
  label: string;
  onSetPinned: (gateId: string, pinned: boolean) => void;
}) {
  return (
    <Popup>
      <div className="cr-min-w-[180px]">
        <div className="cr-flex cr-items-center cr-gap-2">
          <GateStarButton
            pinned={marker.gate.pinned}
            onToggle={() => onSetPinned(marker.gate.id, !marker.gate.pinned)}
          />
          <p className="cr-font-semibold">{marker.gate.name || marker.popupLabel}</p>
        </div>
        <p className="cr-text-xs cr-text-gray-500">
          Gate #{marker.order}
        </p>
        <p className="cr-text-xs cr-text-gray-500">{label}</p>
        <p className="cr-text-sm cr-text-gray-600">
          {marker.isValid ? 'Valid' : 'Not yet validated'}
        </p>
        {marker.gate.sessions && marker.gate.sessions.length > 0 && (
          <div className="cr-mt-2 cr-border-t cr-pt-2">
            <p className="cr-text-xs cr-font-semibold">Sessions:</p>
            {marker.gate.sessions.slice(0, 3).map((s, i) => (
              <p key={i} className="cr-text-xs cr-text-gray-600">
                {new Date(s.date).toLocaleDateString()} -{' '}
                {s.description}
              </p>
            ))}
            {marker.gate.sessions.length > 3 && (
              <p className="cr-text-xs cr-italic cr-text-gray-500">
                ...and {marker.gate.sessions.length - 3} more
              </p>
            )}
          </div>
        )}
      </div>
    </Popup>
  );
}

function RouteRow({
  route,
  isSelected,
  editMode,
  activeGateId,
  isMerging,
  gateOrder,
  onToggleSelect,
  onFocusGate,
  onMergeIdentical,
  onUpdateRoute,
  onUpdateGate,
  onSetGatePinned,
}: {
  route: RouteWithGates;
  isSelected: boolean;
  editMode: boolean;
  activeGateId: string | null;
  isMerging: boolean;
  gateOrder: Map<string, number>;
  onToggleSelect: () => void;
  onFocusGate: (gateId: string) => void;
  onMergeIdentical: () => void;
  onUpdateRoute: (data: { description: string; notes: string | null }) => void;
  onUpdateGate: (gateId: string, data: Pick<Partial<Gate>, 'name' | 'notes'>) => void;
  onSetGatePinned: (gateId: string, pinned: boolean) => void;
}) {
  const hasMultipleGates = (route.gates?.length ?? 0) > 1;
  const [isEditing, setIsEditing] = useState(false);

  const save = (description: string, notes: string | null) => {
    if (description !== (route.description ?? '') || notes !== (route.notes ?? null)) {
      onUpdateRoute({ description, notes });
    }
    setIsEditing(false);
  };

  return (
    <div
      className={
        'cr-border-b cr-border-gray-100 last:cr-border-b-0' +
        (isSelected ? ' cr-bg-cirrusBlue-25' : '')
      }
    >
      {/* Route header */}
      <div
        className={
          'cr-flex cr-items-center cr-justify-between cr-p-4 ' +
          'cr-transition-colors hover:cr-bg-gray-50'
        }
      >
        <div className="cr-flex cr-items-center cr-gap-3">
          {editMode && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={onToggleSelect}
              className={
                'cr-h-4 cr-w-4 cr-rounded cr-border-gray-300 ' +
                'cr-text-cirrusBlue-100 ' +
                'focus:cr-ring-cirrusBlue-100'
              }
            />
          )}
          <span className="cr-font-medium">
            {route.description || route.address}
          </span>
          {!isEditing && <EditPencil onClick={() => setIsEditing(true)} title="Edit route name and notes" />}
          {routeHasPinnedGate(route) && (
            <HiStar
              className="cr-h-4 cr-w-4 cr-text-amber-400"
              title="Has a pinned gate"
            />
          )}
          <span className="cr-text-sm cr-text-gray-500">
            {route.gate_count} gate
            {route.gate_count !== 1 ? 's' : ''}
          </span>
          <span className="cr-text-sm cr-text-gray-500">
            {route.total_session_count} session
            {route.total_session_count !== 1 ? 's' : ''}
          </span>
          {route.average_count != null &&
            route.average_count > 1 && (
              <span
                className={
                  'cr-rounded-full cr-bg-blue-100 cr-px-2 ' +
                  'cr-py-0.5 cr-text-xs cr-font-medium ' +
                  'cr-text-blue-800'
                }
              >
                avg: {route.average_count}
              </span>
            )}
        </div>
        <div className="cr-flex cr-items-center cr-gap-2">
          {editMode && hasMultipleGates && (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                onMergeIdentical();
              }}
              disabled={isMerging}
            >
              {isMerging ? 'Merging...' : 'Merge Identical Gates'}
            </Button>
          )}
        </div>
      </div>

      {/* Route name and notes editor (pencil), or the notes when there are any */}
      {isEditing ? (
        <div className="cr-px-4 cr-pb-3 cr-pl-12">
          <NameNotesForm
            name={route.description ?? ''}
            notes={route.notes ?? null}
            nameLabel="Route name"
            namePlaceholder={route.address || 'Route name...'}
            nameMaxLength={1024}
            onSave={save}
            onCancel={() => setIsEditing(false)}
          />
        </div>
      ) : route.notes && (
        <p className="cr-whitespace-pre-line cr-px-4 cr-pb-3 cr-pl-12 cr-text-xs cr-text-gray-500">
          Notes: {route.notes}
        </p>
      )}

      {/* Gate list — always shown; most routes hold one gate, so a per-route
          expander cost a click without saving meaningful space. */}
      {route.gates && route.gates.length > 0 && (
        <div className="cr-border-t cr-border-gray-100 cr-pl-10">
          {route.gates.map((gate) => (
            <GateRow
              key={gate.id}
              gate={gate}
              order={gateOrder.get(gate.id)}
              isActive={gate.id === activeGateId}
              onFocus={() => onFocusGate(gate.id)}
              onUpdate={(data) => onUpdateGate(gate.id, data)}
              onTogglePinned={() =>
                onSetGatePinned(gate.id, !gate.pinned)
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

function GateRow({
  gate,
  order,
  isActive,
  onFocus,
  onUpdate,
  onTogglePinned,
}: {
  gate: Gate;
  order: number | undefined;
  isActive: boolean;
  onFocus: () => void;
  onUpdate: (data: Pick<Partial<Gate>, 'name' | 'notes'>) => void;
  onTogglePinned: () => void;
}) {
  const [isEditing, setIsEditing] = useState(false);

  const save = (name: string, notes: string | null) => {
    const nextName = name || null;
    if (nextName !== (gate.name ?? null) || notes !== (gate.notes ?? null)) {
      onUpdate({ name: nextName, notes });
    }
    setIsEditing(false);
  };

  const bearing =
    gate.route_bearing != null
      ? gate.route_bearing < 0
        ? Math.round(gate.route_bearing + 360)
        : Math.round(gate.route_bearing)
      : null;

  return (
    <div
      onClick={onFocus}
      className={
        'cr-flex cr-cursor-pointer cr-items-center ' +
        'cr-justify-between cr-border-b cr-border-gray-50 cr-p-3 ' +
        'cr-transition-colors last:cr-border-b-0 hover:cr-bg-gray-50' +
        (isActive ? ' cr-bg-cirrusBlue-25' : '')
      }
    >
      <div className="cr-min-w-0 cr-flex-1">
        <div className="cr-flex cr-items-center cr-gap-2">
          <GateNumber order={order} />
          <GateStarButton pinned={gate.pinned} onToggle={onTogglePinned} />
          <span className={'cr-text-sm cr-font-medium' + (gate.name ? '' : ' cr-text-gray-500')}>
            {gate.name || `Gate ${gate.id.slice(0, 8)}...`}
          </span>
          {!isEditing && <EditPencil onClick={() => setIsEditing(true)} title="Edit gate name and notes" />}
        </div>
        {isEditing && (
          <div className="cr-my-2">
            <NameNotesForm
              name={gate.name ?? ''}
              notes={gate.notes ?? null}
              nameLabel="Gate name"
              namePlaceholder="Gate name..."
              nameMaxLength={128}
              onSave={save}
              onCancel={() => setIsEditing(false)}
            />
          </div>
        )}
        <p className="cr-text-xs cr-text-gray-500">
          Distance:{' '}
          {gate.route_distance != null
            ? `${Math.round(gate.route_distance)}m`
            : 'N/A'}
          {' | '}Bearing: {bearing != null ? `${bearing}°` : 'N/A'}
          {' | '}Elevation:{' '}
          {gate.elevation_difference?.toFixed(2) ?? 'N/A'}m
        </p>
        {!isEditing && gate.notes && (
          <p className="cr-whitespace-pre-line cr-text-xs cr-text-gray-500">Notes: {gate.notes}</p>
        )}
        {gate.sessions && gate.sessions.length > 0 && (
          <div className="cr-mt-1">
            <p className="cr-text-xs cr-font-semibold cr-text-gray-700">
              {gate.sessions.length} session
              {gate.sessions.length !== 1 ? 's' : ''}:
            </p>
            <div className="cr-pl-2">
              {gate.sessions.slice(0, 3).map((s, i) => (
                <p key={i} className="cr-text-xs cr-text-gray-600">
                  {new Date(s.date).toLocaleDateString()} -{' '}
                  {s.description}
                </p>
              ))}
              {gate.sessions.length > 3 && (
                <p className="cr-text-xs cr-italic cr-text-gray-500">
                  ...and {gate.sessions.length - 3} more
                </p>
              )}
            </div>
          </div>
        )}
        {(!gate.sessions || gate.sessions.length === 0) && (
          <p className="cr-text-xs cr-italic cr-text-gray-400">
            No sessions
          </p>
        )}
      </div>
      <Button
        onClick={(e) => {
          e.stopPropagation();
          onFocus();
        }}
      >
        Center
      </Button>
    </div>
  );
}

/* ─── Duplicate Analysis Sub-component ─── */

function DuplicateAnalysisPanel({
  dupAnalysis,
  isPending,
  isError,
  expandedGroups,
  onToggleGroup,
  onMergeGroup,
  isMerging,
}: {
  dupAnalysis: import('../../types/Gate').DuplicateAnalysis | null;
  isPending: boolean;
  isError: boolean;
  expandedGroups: Set<number>;
  onToggleGroup: (idx: number) => void;
  onMergeGroup: (gateIds: string[]) => void;
  isMerging: boolean;
}) {
  if (isPending) {
    return (
      <div className="cr-flex cr-items-center cr-justify-center cr-py-12">
        <Spinner isFetching={true} />
        <span className="cr-ml-3 cr-text-sm cr-text-gray-500">
          Analysing gates for duplicates...
        </span>
      </div>
    );
  }

  if (isError || !dupAnalysis) {
    return (
      <div className="cr-py-8 cr-text-center cr-text-red-600">
        Failed to load duplicate analysis. Please try again.
      </div>
    );
  }

  const { duplicate_groups: groups, summary } = dupAnalysis;

  if (groups.length === 0) {
    return (
      <div className="cr-py-8 cr-text-center cr-text-gray-500">
        No near-duplicate gates found (threshold: 10m). Total
        gates analysed: {summary.total_gates}.
      </div>
    );
  }

  return (
    <div className="cr-flex cr-flex-col cr-gap-3">
      <div
        className={
          'cr-rounded-lg cr-bg-amber-50 cr-border ' +
          'cr-border-amber-200 cr-p-4'
        }
      >
        <p className="cr-text-sm cr-font-semibold cr-text-amber-800">
          Found {summary.total_duplicate_groups} duplicate group
          {summary.total_duplicate_groups !== 1 ? 's' : ''}{' '}
          affecting {summary.total_sessions_affected} session
          {summary.total_sessions_affected !== 1 ? 's' : ''}{' '}
          across {summary.total_duplicate_gates} gate
          {summary.total_duplicate_gates !== 1 ? 's' : ''}{' '}
          (of {summary.total_gates} total)
        </p>
      </div>

      {groups.map((group: DuplicateGroup, groupIdx: number) => {
        const isExpanded = expandedGroups.has(groupIdx);
        const primaryGate = group.gates[0];

        return (
          <div
            key={groupIdx}
            className={
              'cr-overflow-hidden cr-rounded-lg ' +
              'cr-border cr-border-amber-200'
            }
          >
            <div
              className={
                'cr-flex cr-items-center cr-bg-amber-50 ' +
                'cr-transition-colors hover:cr-bg-amber-100'
              }
            >
              <button
                onClick={() => onToggleGroup(groupIdx)}
                className={
                  'cr-flex cr-flex-1 cr-items-center ' +
                  'cr-justify-between cr-p-4'
                }
              >
                <div className="cr-flex cr-items-center cr-gap-3">
                  <HiOutlineChevronRight
                    className={
                      'cr-h-5 cr-w-5 cr-text-amber-600 cr-transition-transform' +
                      (isExpanded ? ' cr-rotate-90' : '')
                    }
                  />
                  <span
                    className={
                      'cr-text-lg cr-font-semibold cr-text-amber-900'
                    }
                  >
                    Group {groupIdx + 1}:{' '}
                    {primaryGate.address || 'Unknown location'}
                  </span>
                  <span
                    className={
                      'cr-rounded-full cr-bg-amber-200 cr-px-2.5 ' +
                      'cr-py-0.5 cr-text-xs cr-font-medium ' +
                      'cr-text-amber-800'
                    }
                  >
                    {group.gate_count} gates
                  </span>
                </div>
                <div
                  className={
                    'cr-text-right cr-text-xs cr-text-amber-700'
                  }
                >
                  <span>Start Δ: {group.max_start_delta_m}m</span>
                  <span className="cr-ml-3">
                    End Δ: {group.max_end_delta_m}m
                  </span>
                </div>
              </button>
              <div className="cr-px-4">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onMergeGroup(group.gates.map((g) => g.id));
                  }}
                  disabled={isMerging}
                  className={
                    'cr-rounded-md cr-bg-amber-600 cr-px-3 ' +
                    'cr-py-1.5 cr-text-xs cr-font-semibold ' +
                    'cr-text-white cr-shadow-sm ' +
                    'hover:cr-bg-amber-700 disabled:cr-opacity-50'
                  }
                >
                  {isMerging ? 'Merging...' : 'Merge'}
                </button>
              </div>
            </div>

            {isExpanded && (
              <div className="cr-border-t cr-border-amber-200">
                {group.gates.map((gate, gateIdx) => (
                  <div
                    key={gate.id}
                    className={
                      'cr-border-b cr-border-amber-100 cr-p-4 ' +
                      'last:cr-border-b-0' +
                      (gateIdx === 0 ? ' cr-bg-amber-25' : '')
                    }
                  >
                    <div
                      className={
                        'cr-flex cr-items-start cr-justify-between'
                      }
                    >
                      <div className="cr-pl-7">
                        <p className="cr-font-medium cr-text-gray-900">
                          {gate.route_description ||
                            gate.address ||
                            `Gate ${gate.id.slice(0, 8)}`}
                        </p>
                        <p className="cr-text-xs cr-text-gray-500">
                          Start: [
                          {gate.gates_lat[0]?.toFixed(6)},{' '}
                          {gate.gates_lon[0]?.toFixed(6)}]{' | '}
                          End: [{gate.gates_lat[1]?.toFixed(6)},{' '}
                          {gate.gates_lon[1]?.toFixed(6)}]
                        </p>
                        {gate.route_distance != null && (
                          <p className="cr-text-xs cr-text-gray-500">
                            Distance:{' '}
                            {Math.round(gate.route_distance)}m
                            {gate.route_bearing != null && (
                              <>
                                {' | '}Bearing:{' '}
                                {gate.route_bearing < 0
                                  ? Math.round(
                                      gate.route_bearing + 360,
                                    )
                                  : Math.round(gate.route_bearing)}
                                °
                              </>
                            )}
                          </p>
                        )}
                        <p className="cr-text-xs cr-text-gray-500">
                          Route ID: {gate.route_id.slice(0, 8)}...
                          {' | '}Gate ID: {gate.id.slice(0, 8)}...
                        </p>
                        {gate.sessions.length > 0 ? (
                          <div className="cr-mt-2">
                            <p
                              className={
                                'cr-text-xs cr-font-semibold ' +
                                'cr-text-gray-700'
                              }
                            >
                              {gate.session_count} session
                              {gate.session_count !== 1 ? 's' : ''}:
                            </p>
                            <div className="cr-pl-2">
                              {gate.sessions
                                .slice(0, 5)
                                .map((session, idx) => (
                                  <p
                                    key={idx}
                                    className={
                                      'cr-text-xs cr-text-gray-600'
                                    }
                                  >
                                    {session.date
                                      ? new Date(`${session.date}T00:00:00`).toLocaleDateString()
                                      : ''}{' '}
                                    - {session.description}
                                  </p>
                                ))}
                              {gate.sessions.length > 5 && (
                                <p
                                  className={
                                    'cr-text-xs cr-italic ' +
                                    'cr-text-gray-500'
                                  }
                                >
                                  ...and {gate.sessions.length - 5}{' '}
                                  more
                                </p>
                              )}
                            </div>
                          </div>
                        ) : (
                          <p
                            className={
                              'cr-mt-1 cr-text-xs cr-italic ' +
                              'cr-text-gray-400'
                            }
                          >
                            No sessions
                          </p>
                        )}
                      </div>
                      <div className="cr-flex cr-items-center">
                        <span
                          className={`cr-rounded-full cr-px-2 cr-py-0.5 cr-text-xs cr-font-medium ${
                            gate.session_count > 0
                              ? 'cr-bg-blue-100 cr-text-blue-800'
                              : 'cr-bg-gray-100 cr-text-gray-500'
                          }`}
                        >
                          {gate.session_count} session
                          {gate.session_count !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
